<template>
    <ion-content class="light-light-background" @touchmove="stopEventAtTop" v-scrollbar>
        <ion-grid class="light-light-background p-2">
            <ion-row v-if="!noTitle" class="py-1">
                <ion-col class="fw-b purple-purple-color ion-text-center fs-larger">
                    {{ $t('titleRoute.orderNotificationTimer') }}
                </ion-col>
            </ion-row>

            <ion-accordion-group
                @ionChange="removeInputFocus"
                :value="(!listStore.reminders[listId]?.count && remindersLoaded) ? 'new' : ''"
            >
                <add-order-timer
                    v-if="userStore.permission[PERMISSION_KEY.LIST_REMINDER_CREATE]"
                    css-class="purple-purple-color"
                    :timer="emptyReminder"
                    @save="addTimer"
                    :key="counter"
                />
                <div
                    v-else-if="!listStore.reminders[listId]?.count && remindersLoaded"
                    class="purple-purple-color center-x py-3"
                >
                    {{ $t('messages.list.noReminder') }}
                </div>

                <template v-for="timer of listStore.reminders[listId]?.data" :key="timer">
                    <order-timer
                        css-class="purple-purple-color"
                        :timer="timer"
                        :list-id="listId"
                        @deleteTimer="deleteTimerCheck($event.id)"
                        @editTimer="editTimer"
                    />
                </template>
            </ion-accordion-group>
        </ion-grid>
        <div style="height: 50px;"/>
    </ion-content>
</template>

<script setup lang="ts">
import { IonAccordionGroup, IonCol, IonContent, IonGrid, IonRow } from "@ionic/vue";
import { ref, computed, onMounted } from "vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";

import OrderTimer from "@/components/modal/OrderTimer.vue";
import AddOrderTimer from "@/components/modal/AddOrderTimer.vue";

import { useListStore } from "@/stores/lists";
import { useUserStore } from "@/stores/user";

import { stopEventAtTop } from "@/functions/modal";
import { replaceToast } from "@/functions/baseToast";
import { popAlertConfirm } from "@/functions/alert";
import { removeInputFocus } from "@/functions/others";

import { Reminder } from "@/types/supplier";
import { PERMISSION_KEY } from "@/types/user";

const listStore = useListStore()
const userStore = useUserStore()

const props = defineProps<{
    listId?: string,
    noTitle?: boolean
}>()

const remindersLoaded = ref(false)
const counter = ref<number>(0)
const emptyReminder = ref(new Reminder())

const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))
const listId = computed<string>(() => props.listId ?? String(route.value.params.listId ?? ''))

const editTimer = async (timer: Reminder): Promise<void> => {
    if (!timer.id) return

    const response = await listStore.editReminder(locationId.value, listId.value, timer)
    if (response) await replaceToast({
        message: t('success.editReminder'),
        id: 'edit-reminder-success',
        cssClass: 'toast-offset-70'
    })

    await setup()
}

const addTimer = async (timer: Reminder): Promise<void> => {
    //*Add Timer to list
    const response = await listStore.addReminder(locationId.value, listId.value, timer)

    if (response) await replaceToast({
        message: t('success.addReminder'),
        id: 'add-reminder-success',
        cssClass: 'toast-offset-70'
    })

    await setup()

    emptyReminder.value = new Reminder()
    counter.value++
}

const deleteTimer = async (timerId: string): Promise<void> => {
    const response = await listStore.deleteReminder(locationId.value, listId.value, timerId)

    if (response) await replaceToast({
        message: t('success.deleteReminder'),
        id: 'delete-reminder-success',
        cssClass: 'toast-offset-70'
    })
    await setup()
}

const deleteTimerCheck = async (timerId: string): Promise<void> => {
    await popAlertConfirm({
        message: t('alert.delete.reminder'),
        handler: {
            ok: () => deleteTimer(timerId)
        }
    })
}

const setup = async (): Promise<void> => {
    await listStore.getReminders(locationId.value, listId.value)
    remindersLoaded.value = true
}

onMounted(() => {
    setup()
})
</script>
